/**
 * Customers hub — central entry point for admins to manage customer
 * accounts. Consolidates the previously-separate Companies and Users
 * pages into a single tabbed surface:
 *
 *   1. Companies — organizations (billing accounts) list view.
 *      Reuses `CompaniesPage`'s list view unchanged.
 *   2. Users     — persons/logins list. Reuses `UsersPage` unchanged.
 *
 * The two data models remain fully separate — this is a UI/nav
 * consolidation only. Company detail (`/admin/companies/:id`) and
 * User detail (`/users/:id`) routes are untouched.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Users } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Header } from '../../components/Header';
import CompaniesPage from '../CompaniesPage';
import UsersPage from '../UsersPage';

const VALID_TABS = ['companies', 'users'];
const DEFAULT_TAB = 'companies';

export default function CustomersHubPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const requested = (searchParams.get('tab') || '').toLowerCase();
    const activeTab = VALID_TABS.includes(requested) ? requested : DEFAULT_TAB;

    // Normalise the URL when it arrived with a missing/invalid ?tab so
    // refreshes + shares land consistently.
    useEffect(() => {
        if (requested !== activeTab) {
            const next = new URLSearchParams(searchParams);
            next.set('tab', activeTab);
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleChange = (value) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', value);
        setSearchParams(next);
    };

    return (
        <div className="min-h-screen bg-background" data-testid="customers-hub-page">
            <Header />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-6">
                    <h1 className="text-3xl font-semibold">Customers</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Companies (billing accounts) and the people who log in for them.
                    </p>
                </div>

                <Tabs value={activeTab} onValueChange={handleChange} className="w-full">
                    <TabsList data-testid="customers-hub-tabs">
                        <TabsTrigger
                            value="companies"
                            className="flex items-center gap-2"
                            data-testid="tab-customers-companies"
                        >
                            <Building2 className="h-4 w-4" />
                            Companies
                        </TabsTrigger>
                        <TabsTrigger
                            value="users"
                            className="flex items-center gap-2"
                            data-testid="tab-customers-users"
                        >
                            <Users className="h-4 w-4" />
                            Users
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="companies" className="mt-2" data-testid="tab-content-companies">
                        <CompaniesPage embedded />
                    </TabsContent>

                    <TabsContent value="users" className="mt-2" data-testid="tab-content-users">
                        <UsersPage embedded />
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
